/**
 * THE HEADER LINE (design_handoff_track_polish §4).
 *
 * One row at the top of every screen: where you are and today's date on the
 * left, a back link when there is somewhere to go back TO, a slot the "need
 * you" pill drops into (§6), and the two clocks on the right.
 *
 * It exists as its own module because both the shell and the record screens
 * write to it, and a view importing app.js would close a loop. Everything here
 * touches four elements in index.html and nothing else.
 *
 * WHY IT MATTERS BEYOND TIDINESS: before this, the project record, the enquiry
 * record and Home each drew their own clock pair from their own render, so a
 * record opened at 10:59 kept showing 10:59 while the shell moved on — audit
 * finding 1, two clocks on one screen disagreeing. There is now exactly one
 * pair, on one interval.
 */
import { escapeHtml } from './ui.js';
import { navItem, activeGroups } from './nav.js';

const el = (id) => document.getElementById(id);

// The org's offices (settings.offices, multi-tenant step 4). index.html carries
// ASP's two as the pre-sign-in markup; setOffices() redraws them per org.
let OFFICES = [{ label: 'UK', tz: 'Europe/London' }, { label: 'Chennai', tz: 'Asia/Kolkata' }];

/** This org's offices, for screens that draw their own clocks (v4 Home). */
export const offices = () => OFFICES.slice();
/** "13:33" in an office's time zone. */
export function clockText(tz) { const { h, m } = clockParts(tz); return `${h}:${m}`; }

/** Redraw the header clocks for this org's offices (at most three). */
export function setOffices(list) {
  const ok = (Array.isArray(list) ? list : []).filter((o) => o && typeof o.tz === 'string' && typeof o.label === 'string').slice(0, 3);
  if (!ok.length) return;
  OFFICES = ok;
  const host = document.querySelector('.hdr-clocks');
  if (!host) return;
  host.innerHTML = OFFICES.map((o, i) => `${i ? '<span class="hdr-clock-rule"></span>' : ''}<div class="hdr-clock">
            <span class="hdr-clock-lab">${escapeHtml(o.label)}</span>
            <span class="hdr-clock-time"><span id="clock-${i}" data-clock>–</span></span>
          </div>`).join('');
  updateClocks();
}

function clockParts(tz) {
  const [h, m] = new Date().toLocaleTimeString('en-GB',
    { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz }).split(':');
  return { h, m };
}

/** Repaint both clocks. The colon is its own span so it can blink (§5). */
export function updateClocks() {
  OFFICES.forEach((o, i) => {
    const node = el('clock-' + i) || el(['clock-uk', 'clock-in'][i]);
    if (!node) return;
    const { h, m } = clockParts(o.tz);
    node.innerHTML = `${escapeHtml(h)}<span class="cl">:</span>${escapeHtml(m)}`;
  });
}

/**
 * The left of the header: WHERE YOU ARE, then the date.
 *
 * "HOME", "FINANCE · INVOICES", "PROJECTS · 8570". The group prefix is read out
 * of the rail rather than written twice, so the header and the rail can never
 * disagree about which group a screen belongs to.
 */
export function headerWhere(viewId, detailLabel) {
  const screen = el('hdr-screen');
  if (!screen) return;
  const item = navItem(viewId);
  let group = null;
  for (const g of activeGroups()) {   // the org's own rail (Traverse): ASP's groups for ASP
    if (g.label && g.items.some((it) => it.id === viewId)) { group = g.label; break; }
  }
  const bits = [group, item ? item.label : 'Track'].filter(Boolean).map((x) => x.toUpperCase());
  if (detailLabel) bits.push(String(detailLabel).toUpperCase());
  screen.textContent = bits.join(' · ');
  const now = new Date();
  // "MON 23 SEP" on a phone, "Wednesday 23 September 2026" on a desk (§13). The
  // short form is shorter than the long one dressed in caps by CSS, which is
  // what text-transform alone would have given — and on a 390px screen the
  // difference is the clocks fitting on the same line or not.
  const phone = window.innerWidth <= 760;
  const day = phone
    ? now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    : now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  el('hdr-date').innerHTML = phone
    ? escapeHtml(day)
    : `${escapeHtml(day)} <span class="yr">${now.getFullYear()}</span>`;
}

/**
 * The back link — ONE LEVEL UP, always, and never drawn twice.
 *
 * Screens used to carry their own "← Projects" button; those are gone, because
 * the header is where the way back lives now. A screen that sits two levels
 * deep (the time breakdown inside a project) overwrites this with its own
 * parent on the way in and puts the record's back link back on the way out —
 * whatever was set last is what the header shows.
 *
 * Call with no label to clear it; selectView does that on every route change,
 * so a stale back link cannot survive into the next screen.
 */
export function headerBack(label, onClick) {
  const node = el('hdr-back');
  if (!node) return;
  node.hidden = !label;
  node.textContent = label ? `← ${label}` : '';
  node.onclick = label ? onClick : null;
}

// ONE TICKER for the whole application. A module runs once however many files
// import it, so this cannot become the two competing intervals it replaced.
updateClocks();
setInterval(updateClocks, 30000);
